import type { SVGProps } from 'react';

/**
 * Icon set: stroked SVG paths on a 24-grid, rendered at 16px.
 *
 * Deliberately hand-rolled rather than pulled from a library: a dozen glyphs is
 * not worth 60KB of dependency, and every icon here shares one stroke weight
 * and one grid, which is what makes a set look designed rather than collected.
 *
 * Unicode characters were used for this first — they inherit the text font, so
 * weight, alignment and size drift between glyphs and the result reads as
 * placeholder art no matter how the rest is styled.
 */

export type IconName =
  | 'dashboard' | 'activity' | 'accounts' | 'resellers' | 'users'
  | 'extension' | 'plan' | 'rates' | 'carrier' | 'hash' | 'billing'
  | 'invoice' | 'server' | 'settings' | 'phone' | 'queue' | 'ivr'
  | 'clock' | 'media' | 'chevronRight' | 'chevronDown' | 'chevronUp' | 'external'
  | 'plus' | 'search' | 'check' | 'close' | 'info' | 'warning' | 'trash'
  | 'edit' | 'panelLeft' | 'sun' | 'moon' | 'contrast' | 'logout' | 'arrowLeft' | 'upload'
  | 'callIn' | 'callOut' | 'callInternal' | 'play' | 'pause' | 'globe'
  | 'grip' | 'chevronLeft' | 'mute' | 'headset' | 'mail' | 'redial' | 'speaker' | 'hold' | 'transfer'
  | 'contacts' | 'message' | 'send' | 'history' | 'bookmark' | 'restore';

const PATHS: Record<IconName, string> = {
  dashboard:    'M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z',
  activity:     'M3 12h4l3 8 4-16 3 8h4',
  accounts:     'M3 21V7l6-4 6 4v14M9 21v-5h3v5M21 21V11l-6-4M6 10h.01M6 14h.01',
  resellers:    'M3 7h18M3 12h18M3 17h18M7 3v18',
  users:        'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  extension:    'M9 3v4M15 3v4M9 17v4M15 17v4M3 9h4M3 15h4M17 9h4M17 15h4M7 7h10v10H7z',
  plan:         'M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  rates:        'M3 3v18h18M7 15l4-4 3 3 5-6',
  carrier:      'M4 7h10l-3-3M20 17H10l3 3M4 7l3 3M20 17l-3-3',
  hash:         'M4 9h16M4 15h16M10 3 8 21M16 3l-2 18',
  billing:      'M3 6h18v13H3zM3 10h18M7 15h4',
  invoice:      'M6 2h9l5 5v15H6zM15 2v5h5M9 13h6M9 17h4',
  server:       'M3 4h18v6H3zM3 14h18v6H3zM7 7h.01M7 17h.01',
  settings:     'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.3a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.7 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1',
  phone:        'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2',
  queue:        'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  ivr:          'M9 3H5a2 2 0 0 0-2 2v4M15 3h4a2 2 0 0 1 2 2v4M9 21H5a2 2 0 0 1-2-2v-4M15 21h4a2 2 0 0 0 2-2v-4M8 9h8M8 13h5',
  clock:        'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 6v6l4 2',
  media:        'M9 18V5l12-2v13M9 13l12-2M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
  chevronRight: 'm9 18 6-6-6-6',
  chevronDown:  'm6 9 6 6 6-6',
  chevronUp:    'm18 15-6-6-6 6',
  external:     'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3',
  plus:         'M12 5v14M5 12h14',
  search:       'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3',
  check:        'm20 6-11 11-5-5',
  close:        'M18 6 6 18M6 6l12 12',
  info:         'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 16v-4M12 8h.01',
  warning:      'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0',
  trash:        'M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6',
  edit:         'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z',
  panelLeft:    'M3 3h18v18H3zM9 3v18',
  sun:          'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  moon:         'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8',
  contrast:     'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 2v20',
  logout:       'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  arrowLeft:    'M19 12H5M12 19l-7-7 7-7',
  upload:       'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  /**
   * Direction, as a glyph rather than a word.
   *
   * A call list is read by shape: three words of the same length in the same
   * colour force you to read every row. The arrows point the way the call went --
   * in towards us, out away from us, and both ways for a call that never left the
   * account -- and each is paired with a text label for a screen reader, because
   * an arrow alone is not a direction to somebody who cannot see it.
   */
  callIn:       'M18 6 8 16M14 16H8v-6',
  callOut:      'M6 18 16 8M10 8h6v6',
  callInternal: 'M7 8h11M7 8l3-3M7 8l3 3M17 16H6M17 16l-3-3M17 16l-3 3',
  play:         'M8 5v14l11-7z',
  pause:        'M9 5v14M15 5v14',
  globe:        'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
  chevronLeft:  'm15 18-6-6 6-6',
  // Six dots: something that can be dragged.
  grip:         'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  /** The fixed keys of a desk phone, as the drawn handset shows them (docs/29). */
  mute:         'M12 3a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3M5 11a7 7 0 0 0 14 0M12 18v3M4 4l16 16',
  headset:      'M4 15v-3a8 8 0 0 1 16 0v3M4 15a2 2 0 0 1 2-2h1v7H6a2 2 0 0 1-2-2zM20 15a2 2 0 0 0-2-2h-1v7h1a2 2 0 0 0 2-2z',
  mail:         'M3 6h18v12H3zM3 7l9 6 9-6',
  redial:       'M20 12a8 8 0 0 1-14 5.3M4 12a8 8 0 0 1 14-5.3M18 3v4h-4M6 21v-4h4',
  history:      'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2',
  bookmark:     'M6 3h12v18l-6-4-6 4z',
  restore:      'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5',
  speaker:      'M4 9h4l5-4v14l-5-4H4zM16.5 9a4 4 0 0 1 0 6M19 6.5a8 8 0 0 1 0 11',
  hold:         'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M10 9v6M14 9v6',
  transfer:     'M4 12h12M12 8l4 4-4 4M20 5v14',
  /** An address book: the phonebook (docs/32). */
  contacts:     'M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6zM3 8h3M3 12h3M3 16h3M12.5 11a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M8.5 17a4 4 0 0 1 8 0',
  message:      'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  send:         'm22 2-7 20-4-9-9-4zM22 2 11 13',
};

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 16, ...rest }: IconProps) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.6}
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false"
      style={{ flex: '0 0 auto', display: 'block' }}
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/**
 * The brand mark. Flat, not a gradient: a gradient logo dates a product
 * instantly and competes with the interface for attention.
 */
/** ArcNet's "AN" mark (owner, 18 September 2026), taken from its logo artwork. */
export const AN_MARK_PATH = 'M135.253,285.292c-5.772,11.516-11.244,22.685-17.058,33.673 c-0.64,1.21-3.232,1.93-4.931,1.951c-12.662,0.161-25.327,0.1-37.99,0.085c-8.411-0.01-10.583-3.429-6.861-10.879 c13.756-27.537,27.546-55.058,41.31-82.592c30.203-60.417,60.398-120.839,90.598-181.259c0.372-0.744,0.768-1.477,1.137-2.223 c1.396-2.824,2.462-6.043,6.419-6.113c4.248-0.075,5.333,3.408,6.818,6.337c2.934,5.786,5.797,11.608,8.699,17.411 c21.354,42.709,42.686,85.429,64.123,128.096c1.481,2.947,1.731,5.306,0.138,8.345c-5.405,10.31-10.499,20.781-15.709,31.185 c-0.511-0.176-0.927-0.191-1.011-0.366c-7.918-16.453-16.873-33.821-24.975-50.182c-4.107-8.296-7.815-15.354-12.062-23.586 c-6.714-13.015-13.03-26.237-19.869-39.184c-1.203-2.276-4.058-4.883-6.302-5.031c-1.85-0.122-4.671,2.793-5.789,5.006 c-20.398,40.405-40.604,80.909-60.851,121.391c-6.941,13.878-13.904,27.745-20.814,41.639c-2.396,4.816-1.803,5.7,3.662,5.78 c3.165,0.047,6.331,0.036,9.496,0.073C133.754,284.852,134.075,284.999,135.253,285.292z M136.107,321.003c4.051-12.084,65.105-131.573,69.968-137.16 c4.886-1.75,7.468,2.906,9.561,7.146c13.326,27.002,26.784,53.941,40.451,80.772c1.252,2.458,4.147,5.323,6.524,5.562 c1.845,0.184,4.824-3.057,6.016-5.422c25.888-51.426,51.596-102.943,77.34-154.441c0.596-1.19,1.154-2.405,1.841-3.542 c2.776-4.591,7.709-4.82,10.216-0.184c4.035,7.462,8.043,14.985,11.27,22.809c0.99,2.402,0.128,6.237-1.137,8.798 c-17.383,35.201-34.98,70.295-52.531,105.414c-10.709,21.428-21.508,42.811-32.066,64.312c-2.107,4.292-4.786,6.236-9.675,6.026 c-7.815-0.335-15.662-0.286-23.481-0.015c-4.472,0.156-7.039-1.625-8.973-5.549c-8.974-18.211-18.033-36.381-27.348-54.42 c-1.2-2.324-4.087-5.266-6.156-5.236c-2.099,0.029-4.897,3-6.106,5.344c-9.149,17.744-18.087,35.602-26.84,53.546 c-2.164,4.436-4.818,6.556-9.988,6.348C155.521,320.729,146.025,321.003,136.107,321.003z M325.811,266.539c8.041,16.087,15.635,30.988,22.926,46.034 c2.219,4.579-0.426,8.283-5.633,8.389c-7.307,0.147-12.896,0.095-20.205,0.163c-3.152,0.029-5.727,0.346-5.938-0.101 c-4.123-8.724-4.125-8.723-9.063-18.535C313.313,291.304,319.75,278.684,325.811,266.539z';
export const AN_MARK_VIEWBOX = '62.5 34 311.1 291.6';

/** The mark alone, in the current text colour: for a coloured panel. */
export function AnMark({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={Math.round(size * 0.94)} viewBox={AN_MARK_VIEWBOX} aria-hidden="true"
      style={{ flex: '0 0 auto', display: 'block', fill: 'currentColor' }}>
      <path fillRule="evenodd" d={AN_MARK_PATH} />
    </svg>
  );
}

/**
 * The brand's tile: ArcNet's mark on its green gradient, or a reseller's own brand (migration
 * 0087) -- its logo, or its first letter, on its colours.
 */
export function BrandMark({ size = 26, brand }: {
  size?: number;
  brand?: { name: string; accent: string; gradient_from: string | null; gradient_to: string | null;
            logo_url: string | null; custom: boolean } | null | undefined;
}) {
  const custom = brand?.custom ? brand : null;
  const from = custom ? (custom.gradient_from ?? custom.accent) : 'var(--arc-brand-from)';
  const to = custom ? (custom.gradient_to ?? custom.accent) : 'var(--arc-brand-to)';
  return (
    <span aria-hidden="true" style={{
      width: size, height: size, flex: '0 0 auto', borderRadius: Math.round(size * 0.24),
      background: `linear-gradient(135deg, ${to}, ${from})`, color: '#ffffff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      fontWeight: 700, fontSize: Math.round(size * 0.5),
    }}>
      {custom
        ? (custom.logo_url
          ? <img src={custom.logo_url} alt="" style={{ width: '72%', height: '72%', objectFit: 'contain' }} />
          : custom.name.slice(0, 1).toUpperCase())
        : <AnMark size={Math.round(size * 0.64)} />}
    </span>
  );
}
